/**
 * 控制面 / 数据面示意图。
 *
 * 这张图要回答的问题是「遥测为什么不经过平台」：浏览器与机体直连，
 * 平台只负责账号、权限、注册表与令牌签发。用 HTML 布局 + 绝对定位的连线画出来，
 * 比一张 SVG 更容易在小屏上重排 —— 小屏改为一列，连线随之隐藏。
 */
import { Icon } from '@/components/Icon';
import type { IconName } from '@/content/icons';

function Node({
  icon,
  title,
  lines,
  tone = 'plain',
}: {
  icon: IconName;
  title: string;
  lines: readonly string[];
  tone?: 'plain' | 'accent';
}) {
  return (
    <div
      className={[
        'relative z-10 flex flex-col gap-2 rounded-xl border p-4',
        tone === 'accent' ? 'border-line-strong bg-surface-3' : 'border-line bg-surface-1',
      ].join(' ')}
    >
      <div className="flex items-center gap-2">
        <Icon name={icon} size={16} className="text-fg" />
        <span className="text-sm font-semibold text-fg">{title}</span>
      </div>
      <ul className="space-y-1">
        {lines.map((line) => (
          <li key={line} className="text-xs leading-relaxed text-fg-muted">
            {line}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ArchitectureDiagram({ className }: { className?: string }) {
  return (
    <div className={['relative', className].filter(Boolean).join(' ')}>
      <div className="grid grid-cols-[minmax(0,1fr)] gap-3 sm:gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-3 rounded-2xl border border-line bg-surface-1/60 p-4">
          <span className="label-mono">控制面 · 低频</span>
          <Node
            icon="shield"
            title="平台 Worker"
            lines={['账号与角色', '设备注册表', '接入凭据签发与吊销', '平台设置与阈值']}
            tone="accent"
          />
          <p className="text-xs leading-relaxed text-fg-muted">
            只处理低频的配置类请求。没有遥测数据流经这里，因此它的容量与机体数量无关。
          </p>
        </div>

        <div className="flex flex-col gap-3 rounded-2xl border border-line bg-surface-1/60 p-4">
          <span className="label-mono">数据面 · 高频</span>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
            <Node icon="globe" title="浏览器（值班端）" lines={['机队总览与单机管理', '地图、姿态球、指令下发']} />
            <Node
              icon="cpu"
              title="机体信息接口"
              lines={['REST 快照 + WebSocket 推流', '1 Hz 遥测、0.2–50 Hz 可配', '指令回执']}
            />
          </div>
          <p className="text-xs leading-relaxed text-fg-muted">
            浏览器直接连机体，遥测不经过平台的转发。代价是机体必须对值班端可达；收益是不为高频数据付中转与存储成本。
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-dashed border-line-strong bg-surface-1 px-4 py-3">
        <span className="inline-flex items-center gap-2 text-xs text-fg">
          <Icon name="key" size={14} className="text-fg-muted" />
          一次性凭据：平台签发短期令牌，浏览器拿着它去连机体
        </span>
        <span className="inline-flex items-center gap-2 text-xs text-fg-muted">
          <Icon name="refresh" size={14} />
          令牌到期前 15 s 提前刷新
        </span>
        <span className="inline-flex items-center gap-2 text-xs text-fg-muted">
          <Icon name="database" size={14} />
          平台不存历史航迹
        </span>
      </div>
    </div>
  );
}
